import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { Modal } from "react-bootstrap";
import Header from "./Header";
import Aside from "./Aside";
import Footer from "./Footer";
import Unauthorized from "./Unauthorized";
import FormNuevaCita from "../agenda/FormNuevaCita";
import axios from "../../axios/axiosconf";
import { convertirHoraMilitarA12h, getFechaActual, getElementosHora, ordenarCitasPorFechaHoraAsc } from "../../../resources/Helper";
import { saveAgendaFocusAndNavigate } from "../../../resources/agendaFocusNavigation";

/** Colores por defecto alineados con estado_smile (ver MigracionSeeder / estados_cita). */
const FALLBACK_COLOR_ESTADO_SMILE = {
    0: "#12B8B8",
    1: "#FFCD00",
    2: "#7ECC38",
    3: "#F12B38",
    4: "#808080",
    5: "#00CDF8",
    6: "#BE61FC",
    7: "#ADFF2F"
};

/** Iconos del hero de estadísticas: mismo criterio de color que los estados en agenda. */
const DASH_STAT_ICON = {
    total: { color: FALLBACK_COLOR_ESTADO_SMILE[0], iconClass: "fas fa-calendar-day" },
    asignadas: { color: FALLBACK_COLOR_ESTADO_SMILE[0], iconClass: "fas fa-clipboard-list" },
    confirmadas: { color: FALLBACK_COLOR_ESTADO_SMILE[1], iconClass: "fas fa-check-circle" },
    atendidas: { color: FALLBACK_COLOR_ESTADO_SMILE[2], iconClass: "fas fa-user-check" },
    noCumplidas: { color: FALLBACK_COLOR_ESTADO_SMILE[3], iconClass: "fas fa-user-times" },
    canceladas: { color: FALLBACK_COLOR_ESTADO_SMILE[4], iconClass: "fas fa-times-circle" },
    canceladasWp: { color: "#128C7E", iconClass: "fab fa-whatsapp" },
    reasignadas: { color: FALLBACK_COLOR_ESTADO_SMILE[6], iconClass: "fas fa-exchange-alt" }
};

function StatHeroPill({ label, value, color, iconClass }) {
    return (
        <div className="agenda-dash-stat-pill">
            <div className="agenda-dash-stat-pill__inner">
                <span
                    className="agenda-dash-stat-pill__icon"
                    style={{
                        color,
                        backgroundColor: `${color}22`,
                        border: `1px solid ${color}44`
                    }}
                    aria-hidden="true"
                >
                    <i className={iconClass} />
                </span>
                <div className="agenda-dash-stat-pill__text">
                    <span className="agenda-dash-stat-pill__label">{label}</span>
                    <span className="agenda-dash-stat-pill__value">{value}</span>
                </div>
            </div>
        </div>
    );
}

function parseEstadoSmile(cita) {
    if (cita == null || cita.estado_smile === undefined || cita.estado_smile === null || cita.estado_smile === "") {
        return null;
    }
    const n = parseInt(String(cita.estado_smile), 10);
    return Number.isNaN(n) ? null : n;
}

function esCancelacionWp(cita) {
    return String(cita?.cancelacion_wp ?? "0") === "1" || Number(cita?.cancelacion_wp) === 1;
}

function getEstiloBadgeEstado(cita) {
    if (esCancelacionWp(cita)) {
        const color = "#128C7E";
        return {
            backgroundColor: `${color}22`,
            color: "#2d3742",
            border: `1px solid ${color}88`,
            borderRadius: "999px",
            fontWeight: 700,
            fontSize: "0.74rem",
            padding: "0.25rem 0.65rem",
            display: "inline-block",
            whiteSpace: "nowrap"
        };
    }
    const sm = parseEstadoSmile(cita);
    const raw = cita?.estado_color != null ? String(cita.estado_color).trim() : "";
    const color = raw || (sm != null && FALLBACK_COLOR_ESTADO_SMILE[sm] != null ? FALLBACK_COLOR_ESTADO_SMILE[sm] : "#6c757d");
    return {
        backgroundColor: `${color}22`,
        color: "#2d3742",
        border: `1px solid ${color}88`,
        borderRadius: "999px",
        fontWeight: 700,
        fontSize: "0.74rem",
        padding: "0.25rem 0.65rem",
        display: "inline-block",
        whiteSpace: "nowrap"
    };
}

function textoBadgeEstado(cita, bloqueo) {
    if (bloqueo) return "Bloqueo";
    if (esCancelacionWp(cita)) return "Cancelada WP";
    return cita.estado || "Sin estado";
}

function nombrePaciente(item) {
    const partes = [item.primer_nombre, item.segundo_nombre, item.primer_apellido, item.segundo_apellido].filter(Boolean);
    const desdePaciente = partes.join(" ").replace(/\s+/g, " ").trim();
    if (desdePaciente) return desdePaciente;
    return (item.cita_paciente || "").trim() || "—";
}

function esBloqueo(item) {
    return item?.bloqueo !== undefined && item?.bloqueo !== null && String(item.bloqueo).trim() !== "";
}

function getBloqueoId(item) {
    if (!esBloqueo(item)) return null;
    const n = parseInt(String(item.bloqueo), 10);
    return Number.isNaN(n) ? null : n;
}

/** Igual que Agenda.clicCita: sin paciente vinculado, el asunto va en un solo campo (cita_paciente). */
function nombresFormularioDesdeCita(cita) {
    const citaPac = (cita.cita_paciente || "").trim();
    const idPac = cita.id_paciente;
    const pn = (cita.primer_nombre || "").trim();

    if (citaPac && !idPac) {
        return {
            primerNombre: citaPac,
            segundoNombre: "",
            primerApellido: "",
            segundoApellido: ""
        };
    }

    return {
        primerNombre: pn || citaPac,
        segundoNombre: cita.segundo_nombre || "",
        primerApellido: pn ? (cita.primer_apellido || "") : "",
        segundoApellido: cita.segundo_apellido || ""
    };
}

function AgendaDashboard() {
    const history = useHistory();

    const [hasPermission, setHasPermission] = useState(null);
    const [canCreateCitas, setCanCreateCitas] = useState(false);
    const [loadingData, setLoadingData] = useState(true);

    const [agendas, setAgendas] = useState([]);
    const [fechaSeleccionada, setFechaSeleccionada] = useState(getFechaActual());
    const [agendaSeleccionada, setAgendaSeleccionada] = useState("");
    const [citasDia, setCitasDia] = useState([]);
    const [modalState, setModalState] = useState("hidden");
    const [showModalFechaAgenda, setShowModalFechaAgenda] = useState(false);
    const [modalFecha, setModalFecha] = useState(getFechaActual());
    const [modalAgendaId, setModalAgendaId] = useState("");
    const [showModalAcciones, setShowModalAcciones] = useState(false);
    const [citaAccion, setCitaAccion] = useState(null);
    const [editCitaModalState, setEditCitaModalState] = useState("hidden");
    const [editCitaProps, setEditCitaProps] = useState({});

    const getUserData = () => JSON.parse(localStorage.getItem("userdata") || "{}");

    const validarPermisos = () => {
        const userdata = getUserData();
        let permisos = [];
        if (Array.isArray(userdata?.permissions)) {
            permisos = userdata.permissions.map((permission) => permission.slug);
        } else if (Array.isArray(userdata?.up)) {
            permisos = userdata.up;
        }

        const puedeVerAgenda = permisos.includes("verAgenda");
        const puedeCrearCitas = permisos.includes("crearCitas");
        setCanCreateCitas(puedeCrearCitas);
        setHasPermission(puedeVerAgenda);
        return { userdata, puedeVerAgenda };
    };

    const elegirAgendaPorDefecto = (listado, userdata) => {
        if (!listado.length) return "";
        const favorita = userdata?.agenda_favorita != null ? String(userdata.agenda_favorita).trim() : "";
        const existeFavorita = favorita && listado.some((a) => String(a.id) === favorita);
        if (existeFavorita) return favorita;
        return String(listado[0].id);
    };

    const cargarAgendas = async () => {
        const { userdata, puedeVerAgenda } = validarPermisos();
        if (!puedeVerAgenda || !userdata?.access_token) {
            setLoadingData(false);
            return;
        }

        try {
            const conf = {
                headers: {
                    Authorization: "Bearer " + userdata.access_token
                }
            };
            const response = await axios.get("/agendas-usuario", conf);
            const listado = Array.isArray(response.data) ? response.data : [];
            setAgendas(listado);
            const idDefecto = elegirAgendaPorDefecto(listado, userdata);
            if (idDefecto) {
                setAgendaSeleccionada(idDefecto);
            }
        } catch (error) {
            setAgendas([]);
        }
    };

    const cargarCitas = async () => {
        const userdata = getUserData();
        if (!userdata?.access_token) {
            setLoadingData(false);
            return;
        }

        try {
            setLoadingData(true);
            const conf = {
                headers: {
                    Authorization: "Bearer " + userdata.access_token
                },
                params: {
                    fecha: fechaSeleccionada
                }
            };
            const response = await axios.get("lista-citas-dia", conf);
            setCitasDia(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            setCitasDia([]);
        } finally {
            setLoadingData(false);
        }
    };

    useEffect(() => {
        cargarAgendas();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (hasPermission === false) return;
        if (hasPermission === true) {
            cargarCitas();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasPermission, fechaSeleccionada]);

    const citasFiltradas = useMemo(() => {
        const base = agendaSeleccionada
            ? citasDia.filter((cita) => String(cita.id_users) === String(agendaSeleccionada))
            : citasDia;

        return ordenarCitasPorFechaHoraAsc(base);
    }, [citasDia, agendaSeleccionada]);

    const agendaNombre = useMemo(() => {
        const encontrada = agendas.find((agenda) => String(agenda.id) === String(agendaSeleccionada));
        return encontrada?.nombre || "Sin agenda";
    }, [agendas, agendaSeleccionada]);

    /**
     * Conteos según estado_smile (columna citas):
     * 0 Asignada, 1 Confirmada, 2 Atendida, 3 No Cumplida, 4 Cancelada, 6 Reasignada.
     * Canceladas WP: flag cancelacion_wp (independiente del estado).
     */
    const estadisticas = useMemo(() => {
        const total = citasFiltradas.length;
        let asignadas = 0;
        let confirmadas = 0;
        let atendidas = 0;
        let noCumplidas = 0;
        let canceladas = 0;
        let canceladasWp = 0;
        let reasignadas = 0;
        citasFiltradas.forEach((cita) => {
            const sm = parseEstadoSmile(cita);
            if (sm === 0) asignadas += 1;
            if (sm === 1) confirmadas += 1;
            if (sm === 2) atendidas += 1;
            if (sm === 3) noCumplidas += 1;
            if (sm === 4) canceladas += 1;
            if (sm === 6) reasignadas += 1;
            if (esCancelacionWp(cita)) canceladasWp += 1;
        });
        return {
            total,
            asignadas,
            confirmadas,
            atendidas,
            noCumplidas,
            canceladas,
            canceladasWp,
            reasignadas
        };
    }, [citasFiltradas]);

    const fechaAmigable = useMemo(() => {
        if (!fechaSeleccionada) return "";
        const [year, month, day] = fechaSeleccionada.split("-").map((valor) => parseInt(valor, 10));
        const fecha = new Date(year, (month || 1) - 1, day || 1);
        const texto = fecha.toLocaleDateString("es-CO", {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric"
        });
        return texto.charAt(0).toUpperCase() + texto.slice(1);
    }, [fechaSeleccionada]);

    const irAgendaCalendario = () => {
        if (agendaSeleccionada) {
            history.push(`/agenda?profesional=${agendaSeleccionada}`);
            return;
        }
        history.push("/agenda");
    };

    const irAAgendaDesdeFila = (e, item) => {
        e.preventDefault();
        e.stopPropagation();
        if (!item.id_users || !item.fecha_cita || !item.id) return;
        saveAgendaFocusAndNavigate(history, {
            agendaId: item.id_users,
            agendaNombre: item.nombre_agenda,
            fechaCita: item.fecha_cita,
            horaCita: item.hora_cita,
            citaId: item.id
        });
    };

    const abrirModalAcciones = (item) => {
        setCitaAccion(item);
        setShowModalAcciones(true);
    };

    const cerrarModalAcciones = () => {
        setShowModalAcciones(false);
        setCitaAccion(null);
    };

    /** Misma lógica que Agenda.clicCita / calendario: citas simples usan cita_paciente y contactos de la cita. */
    const abrirEditarCita = (cita) => {
        if (!cita?.id) return;
        const horaRaw = cita.hora_cita && String(cita.hora_cita).includes(":") ? String(cita.hora_cita) : "09:00";
        const [horaAtencionCita, minutoCita, ampmCita] = getElementosHora(horaRaw);
        const nombres = nombresFormularioDesdeCita(cita);
        const tipoDoc = cita.tipo_documento;
        setEditCitaProps({
            profesionalAsignado: cita.id_users,
            ...nombres,
            email: cita.email != null && String(cita.email).trim() !== "" ? String(cita.email) : "",
            celular: cita.celular != null && String(cita.celular).trim() !== "" ? String(cita.celular) : "",
            tipoDocumento: tipoDoc !== undefined && tipoDoc !== null && String(tipoDoc).trim() !== "" ? String(tipoDoc) : "",
            numeroDocumento: cita.numero_documento != null ? String(cita.numero_documento) : "",
            idPaciente: cita.id_paciente || "",
            idCita: cita.id,
            fecha: cita.fecha_cita,
            hora: cita.hora_cita,
            duracionCita: cita.duracion_cita || "",
            estadoCita: cita.id_estado_cita,
            horaAtencionCita,
            minutoCita,
            ampmCita,
            unidadAtencion: cita.id_unidad_atencion != null && cita.id_unidad_atencion !== "" ? String(cita.id_unidad_atencion) : "",
            idConvenios: cita.id_convenios != null && cita.id_convenios !== "" ? String(cita.id_convenios) : "",
            motivoConsulta: cita.motivo_consulta || "",
            citaPaciente: cita.cita_paciente || "",
            estadoPaciente: cita.estado_paciente,
            puedeAtender: String(cita.mostrar_atencion) === "1"
        });
        setEditCitaModalState("visible");
        cerrarModalAcciones();
    };

    const getBotonesAccion = (item) => (
        <button
            type="button"
            className="btn btn-sm citas-dia-icon-btn citas-dia-icon-btn--menu"
            title="Opciones"
            aria-label="Opciones"
            onClick={() => abrirModalAcciones(item)}
        >
            <i className="fas fa-ellipsis-h" />
        </button>
    );

    const abrirNuevaCita = () => {
        if (!canCreateCitas) return;
        setModalState("visible");
    };

    const eliminarBloqueo = async (item) => {
        const bloqueoId = getBloqueoId(item);
        if (!bloqueoId) return;
        const confirmar = window.confirm("¿Deseas eliminar este bloqueo?");
        if (!confirmar) return;

        const userdata = getUserData();
        if (!userdata?.access_token) return;

        try {
            const conf = {
                headers: {
                    Authorization: "Bearer " + userdata.access_token
                },
                params: {
                    id: bloqueoId
                }
            };
            await axios.delete("/bloqueo", conf);
            cerrarModalAcciones();
            await cargarCitas();
        } catch (error) {
            // Sin toast por ahora; mantenemos flujo actual silencioso.
        }
    };

    const abrirModalFechaAgenda = () => {
        setModalFecha(fechaSeleccionada);
        setModalAgendaId(agendaSeleccionada || (agendas[0] ? String(agendas[0].id) : ""));
        setShowModalFechaAgenda(true);
    };

    const aplicarModalFechaAgenda = () => {
        if (modalFecha) setFechaSeleccionada(modalFecha);
        if (modalAgendaId) setAgendaSeleccionada(modalAgendaId);
        setShowModalFechaAgenda(false);
    };

    if (hasPermission === null) {
        return null;
    }

    if (hasPermission === false) {
        return <Unauthorized />;
    }

    const userdata = getUserData();
    const nombreProfesional = `${userdata?.user_name || ""} ${userdata?.user_lastname || ""}`.trim();

    return (
        <div className="hold-transition agenda-dashboard-page">
            <Header />
            <Aside activelink="calendar" />

            <div className="content-wrapper">
                <div className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2 align-items-center">
                            <div className="col-md-7">
                                <ol className="breadcrumb mb-1">
                                    <li className="breadcrumb-item"><Link to="/home">Home</Link></li>
                                    <li className="breadcrumb-item active">Panel Agenda</li>
                                </ol>
                                <h1 className="m-0">Panel de Agenda</h1>
                            </div>
                            <div className="col-md-5 d-flex justify-content-md-end justify-content-start mt-2 mt-md-0">
                                <button type="button" className="btn btn-outline-info agenda-dash-top-btn" onClick={abrirModalFechaAgenda}>
                                    <i className="far fa-calendar-alt mr-1"></i>
                                    Fecha y agenda
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <section className="content">
                    <div className="container-fluid">
                        <div className="card agenda-dash-hero mb-3">
                            <div className="card-body">
                                <div className="row align-items-start">
                                    <div className="col-lg-8">
                                        <p className="mb-0 agenda-dash-hero__title">{agendaNombre}</p>
                                        <p className="mb-2 agenda-dash-hero__date">{fechaAmigable}</p>
                                        <div className="agenda-dash-stats-row">
                                            <StatHeroPill
                                                label="Citas del día"
                                                value={estadisticas.total}
                                                color={DASH_STAT_ICON.total.color}
                                                iconClass={DASH_STAT_ICON.total.iconClass}
                                            />
                                            <StatHeroPill
                                                label="Asignadas"
                                                value={estadisticas.asignadas}
                                                color={DASH_STAT_ICON.asignadas.color}
                                                iconClass={DASH_STAT_ICON.asignadas.iconClass}
                                            />
                                            <StatHeroPill
                                                label="Confirmadas"
                                                value={estadisticas.confirmadas}
                                                color={DASH_STAT_ICON.confirmadas.color}
                                                iconClass={DASH_STAT_ICON.confirmadas.iconClass}
                                            />
                                            <StatHeroPill
                                                label="Atendidas"
                                                value={estadisticas.atendidas}
                                                color={DASH_STAT_ICON.atendidas.color}
                                                iconClass={DASH_STAT_ICON.atendidas.iconClass}
                                            />
                                            <StatHeroPill
                                                label="Canceladas"
                                                value={estadisticas.canceladas}
                                                color={DASH_STAT_ICON.canceladas.color}
                                                iconClass={DASH_STAT_ICON.canceladas.iconClass}
                                            />
                                            <StatHeroPill
                                                label="Canceladas WP"
                                                value={estadisticas.canceladasWp}
                                                color={DASH_STAT_ICON.canceladasWp.color}
                                                iconClass={DASH_STAT_ICON.canceladasWp.iconClass}
                                            />
                                            <StatHeroPill
                                                label="Reasignadas"
                                                value={estadisticas.reasignadas}
                                                color={DASH_STAT_ICON.reasignadas.color}
                                                iconClass={DASH_STAT_ICON.reasignadas.iconClass}
                                            />
                                            <StatHeroPill
                                                label="No cumplidas"
                                                value={estadisticas.noCumplidas}
                                                color={DASH_STAT_ICON.noCumplidas.color}
                                                iconClass={DASH_STAT_ICON.noCumplidas.iconClass}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-lg-4 mt-3 mt-lg-0">
                                        <div className="agenda-dash-quick-links">
                                            <h5>Accesos R&aacute;pidos</h5>
                                            <button type="button" className="btn btn-light btn-block text-left" onClick={irAgendaCalendario}>
                                                <i className="far fa-calendar-alt text-info mr-2"></i> Ir a Agenda
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-light btn-block text-left"
                                                onClick={abrirNuevaCita}
                                                disabled={!canCreateCitas}
                                            >
                                                <i className="fas fa-plus text-info mr-2"></i> Agendar Cita
                                            </button>
                                            <Link className="btn btn-light btn-block text-left" to="/busqueda-citas">
                                                <i className="fas fa-search text-info mr-2"></i> Buscar Citas
                                            </Link>
                                            <Link className="btn btn-light btn-block text-left" to="/control-asistencia">
                                                <i className="fas fa-user-check text-info mr-2"></i> Control Asistencia
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card">
                            <div className="card-header">
                                <h3 className="card-title mb-0">Pr&oacute;ximas citas</h3>
                            </div>
                            <div className="card-body p-0">
                                {loadingData ? (
                                    <div className="p-4 text-center text-muted">Cargando citas...</div>
                                ) : citasFiltradas.length === 0 ? (
                                    <div className="p-4 text-center text-muted">No hay citas para esta fecha y agenda.</div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-hover mb-0 agenda-dash-table">
                                            <thead>
                                                <tr>
                                                    <th>Hora</th>
                                                    <th>Paciente</th>
                                                    <th>Motivo</th>
                                                    <th>Estado</th>
                                                    <th className="text-center" style={{ width: "90px" }}>Acciones</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {citasFiltradas.map((cita) => {
                                                    const bloqueo = esBloqueo(cita);
                                                    const nom = nombrePaciente(cita);
                                                    const paciente = bloqueo
                                                        ? "Bloqueo de agenda"
                                                        : (nom === "—" ? (cita.cita_paciente || "Paciente") : nom);
                                                    const textoEstado = textoBadgeEstado(cita, bloqueo);

                                                    return (
                                                        <tr key={cita.id}>
                                                            <td>{convertirHoraMilitarA12h(cita.hora_cita)}</td>
                                                            <td>{paciente}</td>
                                                            <td>{bloqueo ? "Bloqueo de horario" : (cita.motivo_consulta || "Sin motivo")}</td>
                                                            <td>
                                                                <span style={getEstiloBadgeEstado(cita)}>{textoEstado}</span>
                                                            </td>
                                                            <td className="text-center">{getBotonesAccion(cita)}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            <Modal show={showModalFechaAgenda} onHide={() => setShowModalFechaAgenda(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Fecha y agenda</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="form-group">
                        <label htmlFor="modal-agenda-dash-fecha">Fecha</label>
                        <input
                            id="modal-agenda-dash-fecha"
                            type="date"
                            className="form-control"
                            value={modalFecha}
                            onChange={(e) => setModalFecha(e.target.value)}
                        />
                    </div>
                    <div className="form-group mb-0">
                        <label htmlFor="modal-agenda-dash-agenda">Agenda</label>
                        <select
                            id="modal-agenda-dash-agenda"
                            className="form-control"
                            value={modalAgendaId}
                            onChange={(e) => setModalAgendaId(e.target.value)}
                        >
                            {agendas.map((agenda) => (
                                <option key={agenda.id} value={agenda.id}>{agenda.nombre}</option>
                            ))}
                        </select>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-secondary" onClick={() => setShowModalFechaAgenda(false)}>
                        Cancelar
                    </button>
                    <button type="button" className="btn btn-info" onClick={aplicarModalFechaAgenda}>
                        Aplicar
                    </button>
                </Modal.Footer>
            </Modal>

            <Modal show={showModalAcciones} onHide={cerrarModalAcciones} centered dialogClassName="citas-dia-actions-modal">
                <Modal.Header className="citas-dia-actions-modal__header">
                    <Modal.Title className="citas-dia-actions-modal__title">
                        <span className="citas-dia-actions-modal__title-icon">
                            <i className="fas fa-calendar-check"></i>
                        </span>
                        Acciones de la cita
                    </Modal.Title>
                    <button type="button" className="citas-dia-actions-modal__close" onClick={cerrarModalAcciones}>
                        <i className="fas fa-times"></i>
                    </button>
                </Modal.Header>
                <Modal.Body className="citas-dia-actions-modal__body">
                    {citaAccion && (
                        esBloqueo(citaAccion) ? (
                            <>
                                <div className="citas-dia-actions-modal__cita">
                                    <span className="citas-dia-actions-modal__avatar">
                                        <i className="fas fa-ban"></i>
                                    </span>
                                    <div>
                                        <div className="citas-dia-actions-modal__cita-name">Bloqueo de agenda</div>
                                        <div className="citas-dia-actions-modal__cita-meta">
                                            {convertirHoraMilitarA12h(citaAccion.hora_cita)} &middot; {citaAccion.nombre_agenda || "Sin agenda"}
                                        </div>
                                    </div>
                                </div>
                                <div className="d-flex flex-column" style={{ gap: "9px" }}>
                                    {Boolean(citaAccion.id_users && citaAccion.fecha_cita && citaAccion.id) && (
                                        <button
                                            type="button"
                                            className="btn citas-dia-modal-action"
                                            onClick={(e) => {
                                                irAAgendaDesdeFila(e, citaAccion);
                                                cerrarModalAcciones();
                                            }}
                                        >
                                            <i className="far fa-calendar-alt mr-2"></i>
                                            Ir a agenda
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="btn citas-dia-modal-action"
                                        onClick={() => eliminarBloqueo(citaAccion)}
                                    >
                                        <i className="fas fa-trash-alt mr-2"></i>
                                        Eliminar bloqueo
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="citas-dia-actions-modal__cita">
                                    <span className="citas-dia-actions-modal__avatar">
                                        {(nombrePaciente(citaAccion).trim().charAt(0) || "C").toUpperCase()}
                                    </span>
                                    <div>
                                        <div className="citas-dia-actions-modal__cita-name">{nombrePaciente(citaAccion)}</div>
                                        <div className="citas-dia-actions-modal__cita-meta">
                                            {convertirHoraMilitarA12h(citaAccion.hora_cita)} &middot; {citaAccion.nombre_agenda || "Sin agenda"}
                                        </div>
                                    </div>
                                </div>
                                <div className="d-flex flex-column" style={{ gap: "9px" }}>
                                    {Boolean(citaAccion.id_users && citaAccion.fecha_cita && citaAccion.id) && (
                                        <button
                                            type="button"
                                            className="btn citas-dia-modal-action"
                                            onClick={(e) => {
                                                irAAgendaDesdeFila(e, citaAccion);
                                                cerrarModalAcciones();
                                            }}
                                        >
                                            <i className="far fa-calendar-alt mr-2"></i>
                                            Ir a agenda
                                        </button>
                                    )}
                                    {Boolean(citaAccion.id) && (
                                        <button
                                            type="button"
                                            className="btn citas-dia-modal-action"
                                            onClick={() => abrirEditarCita(citaAccion)}
                                        >
                                            <i className="far fa-edit mr-2"></i>
                                            Editar cita
                                        </button>
                                    )}
                                    {!citaAccion.id_paciente ? (
                                        <button type="button" className="btn citas-dia-modal-action disabled" disabled>
                                            <i className="far fa-id-card mr-2"></i>
                                            Sin ficha paciente
                                        </button>
                                    ) : String(citaAccion.estado_paciente) === "1" ? (
                                        <Link
                                            to={"/atender-paciente?id=" + citaAccion.id_paciente}
                                            className="btn citas-dia-modal-action"
                                            onClick={cerrarModalAcciones}
                                        >
                                            <i className="fas fa-user-md mr-2"></i>
                                            Atender
                                        </Link>
                                    ) : (
                                        <Link
                                            to={"/editar-paciente?id=" + citaAccion.id_paciente}
                                            className="btn citas-dia-modal-action"
                                            onClick={cerrarModalAcciones}
                                        >
                                            <i className="far fa-edit mr-2"></i>
                                            Completar anamnesis
                                        </Link>
                                    )}
                                </div>
                            </>
                        )
                    )}
                </Modal.Body>
            </Modal>

            <FormNuevaCita
                modalState={modalState}
                closeModal={() => setModalState("hidden")}
                profesionalAsignado={agendaSeleccionada || ""}
                usuarioAsigna={userdata?.user_id || ""}
                nombreProfesionalAsignado={agendaNombre}
                nombreUsuarioAsigna={nombreProfesional}
                fecha={fechaSeleccionada}
                bloquearProfesional={false}
                reloadCitasAgenda={cargarCitas}
            />

            <FormNuevaCita
                modalState={editCitaModalState}
                closeModal={() => setEditCitaModalState("hidden")}
                profesionalAsignado={editCitaProps.profesionalAsignado}
                usuarioAsigna={userdata?.user_id || ""}
                nombreUsuarioAsigna={nombreProfesional}
                primerNombre={editCitaProps.primerNombre}
                segundoNombre={editCitaProps.segundoNombre}
                primerApellido={editCitaProps.primerApellido}
                segundoApellido={editCitaProps.segundoApellido}
                email={editCitaProps.email}
                celular={editCitaProps.celular}
                tipoDocumento={editCitaProps.tipoDocumento}
                numeroDocumento={editCitaProps.numeroDocumento}
                verCita={true}
                puedeAtender={editCitaProps.puedeAtender === true}
                idPaciente={editCitaProps.idPaciente}
                primeraVez={true}
                idCita={editCitaProps.idCita}
                reloadCitasAgenda={cargarCitas}
                stepCita={'20'}
                fecha={editCitaProps.fecha}
                hora={editCitaProps.hora}
                duracionCita={editCitaProps.duracionCita}
                estadoCita={editCitaProps.estadoCita}
                horaAtencionCita={editCitaProps.horaAtencionCita}
                minutoCita={editCitaProps.minutoCita}
                ampmCita={editCitaProps.ampmCita}
                unidadAtencion={editCitaProps.unidadAtencion}
                idConvenios={editCitaProps.idConvenios}
                motivoConsulta={editCitaProps.motivoConsulta}
                citaPaciente={editCitaProps.citaPaciente}
                estadoPaciente={editCitaProps.estadoPaciente}
            />

            <Footer showToast={false} delay={0} />

            <style>{`
                .agenda-dash-hero {
                    border: 1px solid #dee2e6;
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                }
                .agenda-dash-hero .card-body {
                    background: #fff;
                    border-left: 4px solid #17a2b8;
                    padding: 18px 20px;
                }
                .agenda-dash-hero__title {
                    font-size: 1.25rem;
                    font-weight: 800;
                    color: #323a45;
                    line-height: 1.3;
                }
                .agenda-dash-hero__date {
                    font-size: 0.95rem;
                    color: #6c757d;
                    margin-top: 6px;
                    font-weight: 500;
                }
                .agenda-dash-stats-row {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 8px;
                    margin-top: 10px;
                }
                .agenda-dash-stat-pill {
                    flex: 1 1 calc(25% - 8px);
                    min-width: 100px;
                    background: #f8f9fa;
                    border: 1px solid #e9ecef;
                    border-radius: 10px;
                    padding: 8px 10px;
                }
                .agenda-dash-stat-pill__inner {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    min-width: 0;
                }
                .agenda-dash-stat-pill__icon {
                    flex-shrink: 0;
                    width: 40px;
                    height: 40px;
                    border-radius: 10px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.05rem;
                }
                .agenda-dash-stat-pill__text {
                    min-width: 0;
                    flex: 1;
                }
                .agenda-dash-stat-pill__label {
                    display: block;
                    font-size: 0.65rem;
                    text-transform: uppercase;
                    font-weight: 700;
                    color: #6c757d;
                    letter-spacing: 0.02em;
                    margin-bottom: 2px;
                }
                .agenda-dash-stat-pill__value {
                    font-size: 1.15rem;
                    font-weight: 800;
                    color: #323a45;
                    line-height: 1.2;
                }
                @media (max-width: 575px) {
                    .agenda-dash-stat-pill {
                        flex: 1 1 calc(50% - 8px);
                    }
                }
                .agenda-dash-quick-links {
                    background: #f8f9fa;
                    border: 1px solid #e9ecef;
                    border-radius: 10px;
                    padding: 12px;
                }
                .agenda-dash-quick-links h5 {
                    font-size: 0.95rem;
                    font-weight: 700;
                    color: #495057;
                    margin-bottom: 10px;
                }
                .agenda-dash-quick-links .btn {
                    border-radius: 8px;
                    border: 1px solid #dee2e6;
                    font-weight: 600;
                    color: #343a40;
                }
                .agenda-dash-quick-links .btn:hover {
                    background: #e9ecef;
                }
                .agenda-dash-table th {
                    font-size: 0.78rem;
                    text-transform: uppercase;
                    letter-spacing: .02em;
                    color: #6e7d8f;
                    border-top: 0;
                }
                .agenda-dash-table td {
                    vertical-align: middle;
                    font-size: 0.9rem;
                }
                .agenda-dash-top-btn {
                    border-radius: 10px;
                    font-weight: 600;
                }
                .citas-dia-icon-btn {
                    border: 1px solid transparent;
                    border-radius: 10px;
                    width: 31px;
                    height: 31px;
                    padding: 0;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all .15s ease;
                }
                .citas-dia-icon-btn--menu {
                    background: rgba(50, 58, 69, 0.1);
                    border-color: rgba(50, 58, 69, 0.18);
                    color: #2f3945;
                }
                .citas-dia-icon-btn--menu:hover {
                    background: rgba(50, 58, 69, 0.16);
                    color: #202830;
                    transform: translateY(-1px);
                }
                .citas-dia-actions-modal .modal-content {
                    border: none;
                    border-radius: 14px;
                    box-shadow: 0 18px 40px rgba(20, 33, 48, 0.28);
                    overflow: hidden;
                }
                .citas-dia-actions-modal__header {
                    border-bottom: 1px solid #edf2f7;
                    padding: 12px 14px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }
                .citas-dia-actions-modal__title {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 1.03rem;
                    font-weight: 700;
                    color: #2b3440;
                }
                .citas-dia-actions-modal__title-icon {
                    width: 28px;
                    height: 28px;
                    border-radius: 8px;
                    background: rgba(23, 162, 184, 0.13);
                    color: #148495;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.88rem;
                }
                .citas-dia-actions-modal__close {
                    border: 0;
                    background: rgba(50, 58, 69, 0.08);
                    color: #4b5663;
                    width: 30px;
                    height: 30px;
                    border-radius: 999px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                }
                .citas-dia-actions-modal__body { padding: 14px; }
                .citas-dia-actions-modal__cita {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    margin-bottom: 12px;
                    padding: 10px;
                    border: 1px solid #edf2f7;
                    border-radius: 11px;
                    background: #fafcfe;
                }
                .citas-dia-actions-modal__avatar {
                    width: 34px;
                    height: 34px;
                    border-radius: 50%;
                    background: rgba(23, 162, 184, 0.16);
                    color: #147f90;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 700;
                }
                .citas-dia-actions-modal__cita-name {
                    font-size: 0.9rem;
                    font-weight: 700;
                    color: #2e3742;
                    line-height: 1.2;
                }
                .citas-dia-actions-modal__cita-meta {
                    font-size: 0.76rem;
                    color: #6f7d8b;
                    margin-top: 2px;
                }
                .citas-dia-modal-action {
                    border-radius: 12px;
                    font-weight: 700;
                    text-align: left;
                    border: 1px solid rgba(50, 58, 69, 0.08);
                    min-height: 42px;
                    transition: all .16s ease;
                    background: rgba(50, 58, 69, 0.04);
                    color: #3f4a56;
                }
                .citas-dia-modal-action:hover {
                    transform: translateY(-1px);
                    background: rgba(50, 58, 69, 0.08);
                    color: #28313a;
                }
            `}</style>
        </div>
    );
}

export default AgendaDashboard;
